import type { BadStatusError } from '@confluence/network';
import {
	apolloCompatibleMutation,
	type ApolloCompatibleMutationResult,
} from '@confluence/apollo-relay-interop/entry-points/apolloCompatibleMutation.ts';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop.ts';

import type { FetchOptions } from './fetchResponse.ts';
import { fetchResponse } from './fetchResponse.ts';
import type { TransformErrorType } from './handleError.ts';
import { convertToApolloError } from './convertToApolloError.ts';

type GetExperimentalMutationParams<TData, TVariables, TResponse> = FetchOptions<
	TVariables,
	TResponse
> & {
	variables: TVariables;
	parseResult: (response: TResponse, variables: TVariables) => TData | Promise<TData>;
	onCompleted?: (data: TData) => void;
	onError?: (error: ApolloErrorInterop) => void;
	transformError?: TransformErrorType;
};

export async function experimentalMutate<TData, TVariables, TResponse>({
	url,
	method,
	headers,
	body,
	credentials,
	variables,
	parseResult,
	parseResponseOnSuccess,
	parseResponseOnError,
	onCompleted,
	onError,
	transformError,
}: GetExperimentalMutationParams<TData, TVariables, TResponse>): Promise<
	ApolloCompatibleMutationResult<TData>
> {
	const fetchData = async () => {
		const json = await fetchResponse<TVariables, TResponse>(
			{
				url,
				method,
				body,
				headers,
				credentials,
				parseResponseOnSuccess,
				parseResponseOnError,
			},
			variables,
		);

		return await parseResult(json, variables);
	};

	const handleConvertToApolloError = async (error: Error | BadStatusError) => {
		return convertToApolloError((await transformError?.(error)) || error);
	};

	return apolloCompatibleMutation<TData>({
		fetchData,
		onCompleted,
		onError,
		convertToApolloError: handleConvertToApolloError,
	});
}
